import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, Video, FileText, Heart, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminContentManager = () => {
  const { token } = useAuth();
  const [articles, setArticles] = useState([]);
  const [videos, setVideos] = useState([]);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [articleDialog, setArticleDialog] = useState({ open: false, mode: 'create', data: null });
  const [videoDialog, setVideoDialog] = useState({ open: false, mode: 'create', data: null });
  const [storyDialog, setStoryDialog] = useState({ open: false, mode: 'create', data: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const [articlesRes, videosRes, storiesRes] = await Promise.all([
        axios.get(`${API}/admin/discovery/articles`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/discovery/videos`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/discovery/stories`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setArticles(articlesRes.data);
      setVideos(videosRes.data);
      setStories(storiesRes.data);
    } catch (error) {
      console.error('Failed to fetch content:', error);
    } finally {
      setLoading(false);
    }
  };

  // Article handlers
  const handleSaveArticle = async (formData) => {
    try {
      if (articleDialog.mode === 'create') {
        await axios.post(`${API}/admin/discovery/articles`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Article created successfully');
      } else {
        await axios.put(`${API}/admin/discovery/articles/${articleDialog.data.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Article updated successfully');
      }
      setArticleDialog({ open: false, mode: 'create', data: null });
      fetchContent();
    } catch (error) {
      toast.error('Failed to save article');
    }
  };

  // Video handlers
  const handleSaveVideo = async (formData) => {
    try {
      if (videoDialog.mode === 'create') {
        await axios.post(`${API}/admin/discovery/videos`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Video created successfully');
      } else {
        await axios.put(`${API}/admin/discovery/videos/${videoDialog.data.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Video updated successfully');
      }
      setVideoDialog({ open: false, mode: 'create', data: null });
      fetchContent();
    } catch (error) {
      toast.error('Failed to save video');
    }
  };

  // Story handlers
  const handleSaveStory = async (formData) => {
    try {
      if (storyDialog.mode === 'create') {
        await axios.post(`${API}/admin/discovery/stories`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Story created successfully');
      } else {
        await axios.put(`${API}/admin/discovery/stories/${storyDialog.data.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Story updated successfully');
      }
      setStoryDialog({ open: false, mode: 'create', data: null });
      fetchContent();
    } catch (error) {
      toast.error('Failed to save story');
    }
  };

  // Delete handler
  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/discovery/${deleteDialog.type}/${deleteDialog.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Item deleted successfully');
      setDeleteDialog({ open: false, type: null, id: null });
      fetchContent();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  if (loading) {
    return <div className="animate-pulse text-muted-foreground text-center py-8">Loading content...</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-content-manager">
      <div className="flex justify-between items-center">
        <h2 className="font-heading text-2xl">Discovery Content Manager</h2>
      </div>

      <Tabs defaultValue="articles" className="space-y-6">
        <TabsList className="bg-card rounded-xl p-1">
          <TabsTrigger value="articles" className="rounded-lg" data-testid="content-tab-articles">
            <FileText className="h-4 w-4 mr-2" />
            Articles ({articles.length})
          </TabsTrigger>
          <TabsTrigger value="videos" className="rounded-lg" data-testid="content-tab-videos">
            <Video className="h-4 w-4 mr-2" />
            Videos ({videos.length})
          </TabsTrigger>
          <TabsTrigger value="stories" className="rounded-lg" data-testid="content-tab-stories">
            <Heart className="h-4 w-4 mr-2" />
            Stories ({stories.length})
          </TabsTrigger>
        </TabsList>

        {/* Articles Tab */}
        <TabsContent value="articles">
          <div className="bg-card rounded-2xl shadow-sm p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-medium">Articles & Insights</h3>
              <Button
                onClick={() => setArticleDialog({ open: true, mode: 'create', data: null })}
                className="rounded-full"
                data-testid="add-article-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Article
              </Button>
            </div>
            
            {articles.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No articles yet. Add your first article!</p>
            ) : (
              <div className="space-y-4">
                {articles.map((article) => (
                  <div
                    key={article.id}
                    className="flex items-center justify-between p-4 border border-border rounded-xl"
                    data-testid={`article-item-${article.id}`}
                  >
                    <div className="flex items-center gap-4">
                      {article.image_url && (
                        <img
                          src={article.image_url}
                          alt={article.title}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{article.title}</h4>
                          {!article.published && (
                            <Badge variant="secondary" className="text-xs">
                              <EyeOff className="h-3 w-3 mr-1" />
                              Draft
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-1">{article.excerpt}</p>
                        <span className="text-xs text-accent capitalize">{article.category}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setArticleDialog({ open: true, mode: 'edit', data: article })}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => setDeleteDialog({ open: true, type: 'articles', id: article.id })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Videos Tab */}
        <TabsContent value="videos">
          <div className="bg-card rounded-2xl shadow-sm p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-medium">Videos</h3>
              <Button
                onClick={() => setVideoDialog({ open: true, mode: 'create', data: null })}
                className="rounded-full"
                data-testid="add-video-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Video
              </Button>
            </div>
            
            {videos.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No videos yet. Add your first video!</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {videos.map((video) => (
                  <div
                    key={video.id}
                    className="p-4 border border-border rounded-xl"
                    data-testid={`video-item-${video.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Video className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">{video.title}</h4>
                            {!video.published && (
                              <Badge variant="secondary" className="text-xs">Draft</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2">{video.description}</p>
                          <span className="text-xs text-accent capitalize">{video.category}</span>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setVideoDialog({ open: true, mode: 'edit', data: video })}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={() => setDeleteDialog({ open: true, type: 'videos', id: video.id })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Stories Tab */}
        <TabsContent value="stories">
          <div className="bg-card rounded-2xl shadow-sm p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-medium">Testimonials</h3>
              <Button
                onClick={() => setStoryDialog({ open: true, mode: 'create', data: null })}
                className="rounded-full"
                data-testid="add-story-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Story
              </Button>
            </div>
            
            {stories.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No stories yet. Add your first success story!</p>
            ) : (
              <div className="space-y-4">
                {stories.map((story) => (
                  <div
                    key={story.id}
                    className="p-4 border border-border rounded-xl"
                    data-testid={`story-item-${story.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{story.title}</h4>
                          {!story.published && (
                            <Badge variant="secondary" className="text-xs">Draft</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2 mt-1">"{story.content}"</p>
                        <p className="text-xs text-accent mt-2">— {story.author_name}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setStoryDialog({ open: true, mode: 'edit', data: story })}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={() => setDeleteDialog({ open: true, type: 'stories', id: story.id })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Article Dialog */}
      <ArticleDialog
        open={articleDialog.open}
        mode={articleDialog.mode}
        data={articleDialog.data}
        onClose={() => setArticleDialog({ open: false, mode: 'create', data: null })}
        onSave={handleSaveArticle}
      />

      {/* Video Dialog */}
      <VideoDialog
        open={videoDialog.open}
        mode={videoDialog.mode}
        data={videoDialog.data}
        onClose={() => setVideoDialog({ open: false, mode: 'create', data: null })}
        onSave={handleSaveVideo}
      />

      {/* Story Dialog */}
      <StoryDialog
        open={storyDialog.open}
        mode={storyDialog.mode}
        data={storyDialog.data}
        onClose={() => setStoryDialog({ open: false, mode: 'create', data: null })}
        onSave={handleSaveStory}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, type: null, id: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">Are you sure you want to delete this item? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, type: null, id: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Article Dialog Component
const ArticleDialog = ({ open, mode, data, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: 'insight',
    image_url: '',
    published: true
  });

  useEffect(() => {
    if (data) {
      setFormData({
        title: data.title || '',
        excerpt: data.excerpt || '',
        content: data.content || '',
        category: data.category || 'insight',
        image_url: data.image_url || '',
        published: data.published !== false
      });
    } else {
      setFormData({
        title: '',
        excerpt: '',
        content: '',
        category: 'insight',
        image_url: '',
        published: true
      });
    }
  }, [data, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Add New Article' : 'Edit Article'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              data-testid="article-title-input"
            />
          </div>
          <div>
            <Label htmlFor="excerpt">Excerpt (Short Description)</Label>
            <Textarea
              id="excerpt"
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              rows={2}
              required
              data-testid="article-excerpt-input"
            />
          </div>
          <div>
            <Label htmlFor="content">Full Content</Label>
            <Textarea
              id="content"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              rows={6}
              required
              data-testid="article-content-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="category">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger data-testid="article-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="news">News</SelectItem>
                  <SelectItem value="insight">Insight</SelectItem>
                  <SelectItem value="resource">Resource</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="image_url">Image URL (optional)</Label>
              <Input
                id="image_url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://..."
                data-testid="article-image-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="published"
              checked={formData.published}
              onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
              className="rounded"
            />
            <Label htmlFor="published">Published</Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="save-article-btn">Save Article</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Video Dialog Component
const VideoDialog = ({ open, mode, data, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    video_url: '',
    category: 'educational',
    thumbnail_url: '',
    published: true
  });

  useEffect(() => {
    if (data) {
      setFormData({
        title: data.title || '',
        description: data.description || '',
        video_url: data.video_url || '',
        category: data.category || 'educational',
        thumbnail_url: data.thumbnail_url || '',
        published: data.published !== false
      });
    } else {
      setFormData({
        title: '',
        description: '',
        video_url: '',
        category: 'educational',
        thumbnail_url: '',
        published: true
      });
    }
  }, [data, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Add New Video' : 'Edit Video'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="video-title">Title</Label>
            <Input
              id="video-title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              data-testid="video-title-input"
            />
          </div>
          <div>
            <Label htmlFor="video-description">Description</Label>
            <Textarea
              id="video-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              required
              data-testid="video-description-input"
            />
          </div>
          <div>
            <Label htmlFor="video-url">Video URL (YouTube or Vimeo)</Label>
            <Input
              id="video-url"
              value={formData.video_url}
              onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
              placeholder="https://www.youtube.com/watch?v=..."
              required
              data-testid="video-url-input"
            />
            <p className="text-xs text-muted-foreground mt-1">Paste the full YouTube or Vimeo URL</p>
          </div>
          <div>
            <Label htmlFor="video-category">Category</Label>
            <Select
              value={formData.category}
              onValueChange={(value) => setFormData({ ...formData, category: value })}
            >
              <SelectTrigger data-testid="video-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="intro">Introduction</SelectItem>
                <SelectItem value="educational">Educational</SelectItem>
                <SelectItem value="testimonial">Client Story</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="video-published"
              checked={formData.published}
              onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
              className="rounded"
            />
            <Label htmlFor="video-published">Published</Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="save-video-btn">Save Video</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Story Dialog Component
const StoryDialog = ({ open, mode, data, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    author_name: '',
    author_description: '',
    image_url: '',
    published: true
  });

  useEffect(() => {
    if (data) {
      setFormData({
        title: data.title || '',
        content: data.content || '',
        author_name: data.author_name || '',
        author_description: data.author_description || '',
        image_url: data.image_url || '',
        published: data.published !== false
      });
    } else {
      setFormData({
        title: '',
        content: '',
        author_name: '',
        author_description: '',
        image_url: '',
        published: true
      });
    }
  }, [data, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Add New Story' : 'Edit Story'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="story-title">Title</Label>
            <Input
              id="story-title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Finding My Voice Again"
              required
              data-testid="story-title-input"
            />
          </div>
          <div>
            <Label htmlFor="story-content">Story Content</Label>
            <Textarea
              id="story-content"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              rows={5}
              placeholder="The client's story in their words..."
              required
              data-testid="story-content-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="author-name">Author Name</Label>
              <Input
                id="author-name"
                value={formData.author_name}
                onChange={(e) => setFormData({ ...formData, author_name: e.target.value })}
                placeholder="e.g., Sarah"
                required
                data-testid="story-author-input"
              />
            </div>
            <div>
              <Label htmlFor="author-desc">Author Description (optional)</Label>
              <Input
                id="author-desc"
                value={formData.author_description}
                onChange={(e) => setFormData({ ...formData, author_description: e.target.value })}
                placeholder="e.g., Client for 2 years"
                data-testid="story-author-desc-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="story-published"
              checked={formData.published}
              onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
              className="rounded"
            />
            <Label htmlFor="story-published">Published</Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="save-story-btn">Save Story</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdminContentManager;
